/* eslint-disable react-hooks/exhaustive-deps*/
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { verify } from '../../ducks/EmailVerification.duck';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { parse } from '../../util/urlHelpers';
import { ensureCurrentUser } from '../../util/data';
import {
    NamedRedirect,
    NamedLink,
} from '../../components';

import ContainerWrapper from '../../custom-components/wrappers/container-wrapper/ContainerWrapper';
import PageWrapper from '../../custom-components/wrappers/page-wrapper/PageWrapper';
import Footer from '../../custom-components/elements/footer/Footer';

import { marketplaceLockupLogo } from '../../assets/viveka-logos';

import css from './EmailVerificationPage.css';

/**
  Parse verification token from URL

  Returns stringified token, if the token is provided.

  Returns `null` if verification token is not provided.

  Please note that we need to explicitely stringify the token, because
  the unwanted result of the `parse` method is that it automatically
  parses the token to number.
*/
const parseVerificationToken = search => {
    const urlParams = parse(search);
    const verificationToken = urlParams.t;

    if (verificationToken) {
          return `${verificationToken}`;
    }

    return null;
};

export const EmailVerificationPageComponent = props => {
    const {
          currentUser,
          submitVerification,
          emailVerificationInProgress,
          isVerified,
          verificationError,
          location,
    } = props;
    // const title = intl.formatMessage({
    //   id: 'EmailVerificationPage.title',
    // });

    // FIX 1: Moved user declaration BEFORE useEffect to avoid TDZ ReferenceError
    const user = ensureCurrentUser(currentUser);

    const initialValues = {
          verificationToken: parseVerificationToken(location ? location.search : null),
    };

    useEffect(() => {
          // Fire verification whenever a token is present in the URL and the current user is loaded.
          // Previously the guard `emailVerified === false` blocked email-change verification, since
          // emailVerified stays `true` for the old email — only the new pending email needs verifying.
          if (user && user.id && initialValues.verificationToken) {
            submitVerification({ verificationToken: initialValues.verificationToken });
          }
          // Re-run when the user becomes available (currentUser may still be loading on mount,
          // e.g. right after a login redirect).
    }, [user.id?.uuid])

    // Only short-circuit to LandingPage when there's no verification token to process.
    // For email-change flows the user already has emailVerified=true (for the old email),
    // so the previous `if (emailVerified) redirect` ran on mount and skipped verification entirely.
    if (user && user.id && user.attributes.emailVerified && !initialValues.verificationToken) {
          return <NamedRedirect name="LandingPage" />;
    }

    return (
          <PageWrapper>
            <ContainerWrapper>
              <div className={css.outerbase}>
            <div className={css.base}>
            <div className={css.leftDiv}>
              <div className={css.logo}>
                <img alt="Viveka" src={marketplaceLockupLogo} />
  </div>
              <div className={css.verificationEmail}>
                {emailVerificationInProgress ? (
                  <>
                    <div className={css.spinner} aria-hidden="true" />
                    <p className={css.verificationTitle}>Verifying your email...</p>
                    <p className={css.verificationBody}>This will just take a moment.</p>
                  </>
                ) : isVerified ? (
                  <>
                    <div className={css.statusIconSuccess} aria-hidden="true">✓</div>
                    <p className={css.verificationTitle}>Email verified!</p>
                    <p className={css.verificationBody}>
                      Your email is now <strong>{user && user.attributes.email}</strong>.
                    </p>
                    <NamedLink name="LandingPage" className={css.continueButton}>
                      Continue to Viveka
                    </NamedLink>
                  </>
                ) : verificationError ? (
                  <>
                    <div className={css.statusIconError} aria-hidden="true">!</div>
                    <p className={css.verificationTitle}>Verification failed</p>
                    <p className={css.verificationBody}>
                      The link may be expired, already used, or for a different account.
                      Try logging in with the email you wanted to change, then click the most
                      recent verification link.
                    </p>
                    <NamedLink name="LoginPage" className={css.continueButton}>
                      Log in
                    </NamedLink>
                  </>
                ) : (
                  <>
                    <p className={css.verificationTitle}>Verify your email</p>
                    <p className={css.verificationBody}>
                      We have sent you a verification email to <strong>{user && user.attributes.email}</strong>.
                    </p>
                  </>
                )}
              </div>
</div>
            <div className={css.rightDiv}>
              <div>
                  <p> Carve your</p>
                <p> Life the way</p>
                <p> You want it</p>
  </div>
  </div>
  </div>
  </div>
  </ContainerWrapper>

      <ContainerWrapper>
          <Footer />
  </ContainerWrapper>
  </PageWrapper>
  );
};

EmailVerificationPageComponent.defaultProps = {
    currentUser: null,
    verificationError: null,
};

const { bool, func, shape, string } = PropTypes;

EmailVerificationPageComponent.propTypes = {
    currentUser: propTypes.currentUser,
    scrollingDisabled: bool.isRequired,
    submitVerification: func.isRequired,
    emailVerificationInProgress: bool.isRequired,
    verificationError: propTypes.error,

    // from withRouter
    location: shape({
          search: string,
    }).isRequired,

    // from injectIntl
    intl: intlShape.isRequired,
};

const mapStateToProps = state => {
    const { currentUser } = state.user;
    const { verificationError, verificationInProgress, isVerified } = state.EmailVerification;
    return {
          verificationError,
          emailVerificationInProgress: verificationInProgress,
          isVerified,
          currentUser,
          scrollingDisabled: isScrollingDisabled(state),
    };
};

const mapDispatchToProps = dispatch => ({
    submitVerification: ({ verificationToken }) => {
          return dispatch(verify(verificationToken));
    },
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const EmailVerificationPage = compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
    injectIntl
  )(EmailVerificationPageComponent);

EmailVerificationPage.loadData = (params, search) => {
    const token = parseVerificationToken(search);
    return verify(token);
};

export default EmailVerificationPage;
